import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, of, takeUntil, tap } from 'rxjs';
import { getSeverityByStatusCode } from '../../../core/functions/app-insights-functions';
import { AppInsightsService } from '../../../core/services/app-insights/app-insights.service';
import { clearAllState, loadProfileAndPrintPage, printPage } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated, setPatientIdSuccess } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PatientProfilesService } from '../../services/patient-profiles/patient-profiles.service';
import {
  getFullProfile,
  getFullProfileFailure,
  getFullProfileSuccess,
  getProfile,
  getProfileFailure,
  getProfileSuccess,
  saveProfile,
  saveProfileFailure,
  saveProfileSuccess,
} from '../actions/profile.actions';

@Injectable()
export class ProfileEffects {
  private actions$ = inject(Actions);
  private patientProfilesService = inject(PatientProfilesService);
  private appInsightsService = inject(AppInsightsService);
  private store = inject(Store);
  private location = inject(Location);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getProfile, setPatientIdSuccess, loadProfileAndPrintPage),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        const source = 'source' in action ? action.source : 'default';

        return this.patientProfilesService.getPatientProfile(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(profile => getProfileSuccess({ profile, source })),
          catchError(err => of(getProfileFailure({ error: err.error?.errors })))
        );
      })
    );
  });

  saveProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveProfile),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        return this.patientProfilesService.savePatientProfile(patientId, action.profile).pipe(
          concatLatestFrom(() => this.store.select(selectPatientId)),
          // Patient switched mid-write: drop the response so patient A's profile can't land in the reset slice.
          filter(([, currentPatientId]) => currentPatientId === patientId),
          map(([profile]) => {
            this.appInsightsService.trackEvent('User.SaveProfile.Success');
            return saveProfileSuccess({ profile });
          }),
          catchError(err => {
            this.appInsightsService.trackTrace('User.SaveProfile.Failure', getSeverityByStatusCode(err.status));
            return of(saveProfileFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  navigateAfterProfileSave$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(saveProfileSuccess),
        tap(() => {
          this.location.back();
        })
      );
    },
    { dispatch: false }
  );

  printOnProfileLoad$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getProfileSuccess),
      filter(action => action.source === 'print'),
      map(() => printPage())
    );
  });

  getFullProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getFullProfile),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([, patientId]) => {
        return this.patientProfilesService.getFullPatientProfile(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(fullProfile => getFullProfileSuccess({ fullProfile })),
          catchError(err => of(getFullProfileFailure({ error: err.error?.errors })))
        );
      })
    );
  });
}
